import React, { useState } from 'react';
import HrnbButton from '../controls/HrnbButton';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import HrnbInput from '../controls/HrnbInput';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/components/duels/DuelTrackForm.css';
import {worksApi, textsApi, duelsApi, uploadTrack} from '../../api';

/**
 * Компонент загрузки работы для дуэли через Work API
 * Поддерживает три типа:
 * - Audio: только аудиофайл (трек)
 * - Text: только текст
 * - Hybrid: аудио + текст
 * 
 * @component
 * @param {Object} props
 * @param {string} props.duelId - UUID дуэли
 * @param {string} props.roundId - UUID раунда дуэли
 * @param {string} props.participantId - ID участника дуэли
 * @param {string} props.authorId - ID профиля автора
 * @param {Function} props.onSuccess - Колбэк при успешной загрузке
 * @param {Function} props.onCancel - Колбэк при отмене
 * @param {string} [props.duelType='hybrid'] - Тип дуэли: audio, text, hybrid
 */
export default function WorkUploader({
  duelId,
  roundId,
  participantId,
  authorId,
  onSuccess,
  onCancel,
  duelType = 'hybrid'
}) {
  const [workType, setWorkType] = useState(duelType); // 'audio' | 'text' | 'hybrid'
  const [audioFile, setAudioFile] = useState(null);
  const [lyrics, setLyrics] = useState('');
  const [comment, setComment] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Поддерживаемые типы работ
  const supportAudio = workType === 'audio' || workType === 'hybrid';
  const supportText = workType === 'text' || workType === 'hybrid';

  // Валидация формы
  const validateForm = () => {
    if (!title.trim()) {
      setError('Укажите название работы');
      return false;
    }

    // Проверка: либо аудиофайл, либо текст
    if (!audioFile && !lyrics.trim()) {
      if (workType === 'audio') {
        setError('Загрузите аудиофайл');
        return false;
      } else if (workType === 'text') {
        setError('Введите текст трека');
        return false;
      } else {
        setError('Загрузите аудиофайл или введите текст трека');
        return false;
      }
    }

    // Проверка длины текста
    if (lyrics && lyrics.length > 10000) {
      setError('Текст слишком длинный (макс. 10000 символов)');
      return false;
    }

    // Проверка комментария
    if (comment && comment.length > 1000) {
      setError('Комментарий слишком длинный (макс. 1000 символов)');
      return false;
    }

    if (!audioFile && lyrics.length === 0) {
      setError('Загрузите хотя бы один компонент: аудио или текст');
      return false;
    }

    return true;
  };

  // Обработчик загрузки аудиофайла
  const handleFileSelect = (file) => {
    setError(null);
    
    // Проверка типа файла
    if (!file.type.startsWith('audio/')) {
      setError('Загрузите аудиофайл (MP3, WAV, etc.)');
      return;
    }

    // Проверка размера (макс. 30MB)
    if (file.size > 30 * 1024 * 1024) {
      setError('Размер файла не должен превышать 30MB');
      return;
    }

    setAudioFile(file);
  };

  // Обработчик изменения текста
  const handleLyricsChange = (value) => {
    setLyrics(value);
    if (error && value.trim()) {
      setError(null);
    }
  };

  // Обработчик изменения комментария
  const handleCommentChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setComment(value);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let textId = null;
      let trackId = null;

      // 1. Создаём текст, если есть
      if (lyrics.trim()) {
        const textData = {
          duelParticipantId: participantId,
          title: title.trim(),
          description: comment.trim() || null,
          content: lyrics.trim(),
          status: 'SUBMITTED'
        };

        console.log('[WorkUploader] Creating text:', textData);
        const textResult = await textsApi.createText(textData);
        console.log('[WorkUploader] Text created:', textResult);
        textId = textResult.id;
      }

      // 2. Загружаем аудиофайл, если есть
      if (audioFile) {
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('title', title.trim());
        formData.append('description', comment.trim() || '');
        formData.append('authorId', authorId);
        formData.append('roundId', roundId);

        console.log('[WorkUploader] Uploading audio file...');
        const uploadResult = await uploadTrack(duelId, roundId, audioFile, comment);
        console.log('[WorkUploader] Track uploaded:', uploadResult);
        trackId = uploadResult.id;
      }

      // 3. Создаём работу
      const workData = {
        duelParticipantId: participantId,
        duelRoundId: roundId,
        textId: textId,
        trackId: trackId,
        status: 'SUBMITTED'
      };

      console.log('[WorkUploader] Creating work:', workData);
      const workResult = await worksApi.createWork(workData);
      console.log('[WorkUploader] Work created successfully:', workResult);

      if (onSuccess) {
        onSuccess(workResult);
      }
    } catch (err) {
      console.error('[WorkUploader] Failed to upload work:', err);
      console.error('[WorkUploader] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.message || 'Ошибка при загрузке работы');
    } finally {
      setLoading(false);
    }
  };

  // Очистка формы
  const handleClear = () => {
    setAudioFile(null);
    setLyrics('');
    setComment('');
    setTitle('');
    setError(null);
    setProgress(0);
  };

  return (
    <div className="duel-track-form">
      {/* Переключатель типа работы */}
      <div className="duel-track-form__type-toggle" style={{ marginBottom: '20px' }}>
        <button
          type="button"
          className={`type-btn ${workType === 'text' ? 'active' : ''}`}
          onClick={() => { setWorkType('text'); handleClear(); }}
          disabled={duelType === 'audio'}
          style={{
            padding: '10px 20px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: workType === 'text' ? '#007bff' : '#fff',
            color: workType === 'text' ? '#fff' : '#333',
            cursor: duelType === 'audio' ? 'not-allowed' : 'pointer',
            opacity: duelType === 'audio' ? 0.6 : 1
          }}
        >
          📝 Текст
        </button>
        <button
          type="button"
          className={`type-btn ${workType === 'hybrid' ? 'active' : ''}`}
          onClick={() => { setWorkType('hybrid'); handleClear(); }}
          disabled={duelType === 'text' || duelType === 'audio'}
          style={{
            padding: '10px 20px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: workType === 'hybrid' ? '#007bff' : '#fff',
            color: workType === 'hybrid' ? '#fff' : '#333',
            cursor: (duelType === 'text' || duelType === 'audio') ? 'not-allowed' : 'pointer',
            opacity: (duelType === 'text' || duelType === 'audio') ? 0.6 : 1
          }}
        >
          🎵 + 📝 Гибрид
        </button>
        <button
          type="button"
          className={`type-btn ${workType === 'audio' ? 'active' : ''}`}
          onClick={() => { setWorkType('audio'); handleClear(); }}
          disabled={duelType === 'text'}
          style={{
            padding: '10px 20px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: workType === 'audio' ? '#007bff' : '#fff',
            color: workType === 'audio' ? '#fff' : '#333',
            cursor: duelType === 'text' ? 'not-allowed' : 'pointer',
            opacity: duelType === 'text' ? 0.6 : 1
          }}
        >
          🎵 Аудио
        </button>
      </div>

      {error && (
        <HrnbAlert variant="danger" onClose={() => setError(null)} className="duel-track-form__error">
          {error}
        </HrnbAlert>
      )}

      <form onSubmit={handleSubmit} className="duel-track-form__form">
        {/* Название работы */}
        <div className="duel-track-form__field">
          <label className="duel-track-form__label">
            Название работы
            <span className="duel-track-form__required">*</span>
          </label>
          <HrnbInput
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: Мой ответный трек"
            required
            disabled={loading}
          />
        </div>

        {/* Загрузка аудиофайла */}
        {supportAudio && (
          <div className="duel-track-form__field">
            <label className="duel-track-form__label">
              Аудиофайл
              {workType !== 'text' && <span className="duel-track-form__required">*</span>}
            </label>
            <HrnbFileUpload
              accept="audio/*"
              maxSize={30 * 1024 * 1024}
              onFileSelect={handleFileSelect}
              onError={(error) => setError(error.message)}
              label={audioFile ? audioFile.name : 'Выберите аудиофайл'}
              disabled={loading}
            />
            {audioFile && (
              <div className="duel-track-form__file-info">
                <span>{audioFile.name}</span>
                <span className="duel-track-form__file-size">
                  {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                </span>
                <button
                  type="button"
                  className="duel-track-form__remove-btn"
                  onClick={() => setAudioFile(null)}
                  disabled={loading}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}

        {/* Текст трека */}
        {supportText && (
          <div className="duel-track-form__field">
            <label className="duel-track-form__label">
              Текст трека
              {workType === 'text' && <span className="duel-track-form__required">*</span>}
            </label>
            <HrnbTextArea
              value={lyrics}
              onChange={handleLyricsChange}
              placeholder="Введите текст вашего трека (панчлайны, куплеты...)"
              rows={8}
              maxLength={10000}
              showCounter
              disabled={loading}
            />
            <div className="duel-track-form__hint">
              Максимум 10000 символов. Используйте переносы строк для структуры.
            </div>
          </div>
        )}

        {/* Комментарий */}
        <div className="duel-track-form__field">
          <label className="duel-track-form__label">
            Комментарий
          </label>
          <HrnbInput
            type="text"
            value={comment}
            onChange={handleCommentChange}
            placeholder="Комментарий к работе (необязательно)"
            maxLength={1000}
            disabled={loading}
          />
          <div className="duel-track-form__hint">
            Максимум 1000 символов
          </div>
        </div>

        {loading && (
          <div className="duel-track-form__progress">
            <HrnbLoader />
            <p>Загрузка работы... {progress > 0 && `(${progress}%)`}</p>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="duel-track-form__actions">
          {onCancel && (
            <HrnbButton
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Отмена
            </HrnbButton>
          )}
          <HrnbButton
            type="button"
            variant="secondary"
            onClick={handleClear}
            disabled={loading}
          >
            Очистить
          </HrnbButton>
          <HrnbButton
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Отправка...' : 'Отправить работу'}
          </HrnbButton>
        </div>
      </form>
    </div>
  );
}
